import { isEqual, pickBy } from "lodash";

import { EventNames, TrackingEvent } from "lib/tracking";
import { SpendDataFilters } from "./hooks/useSpendUrlState";

export function createViewSelectedEvent(viewName: string): TrackingEvent {
  return {
    name: EventNames.viewSelected,
    data: {
      "Context source": "Quick select",
      "View name": viewName,
    },
  };
}

// The location picker's value holds each pick three ways, so track the one list of picks.
// Sorted: the picker rebuilds that list in its own order when the panel opens.
function trackedFilters({ buyerCountryRegions, ...rest }: SpendDataFilters) {
  if (!buyerCountryRegions) {
    return rest;
  }
  return { ...rest, buyerCountryRegions: [...(buyerCountryRegions.selected ?? [])].sort() };
}

export function createFilterChangedEvents(
  previousFilters: SpendDataFilters,
  nextFilters: SpendDataFilters,
): TrackingEvent[] {
  const previous: Record<string, unknown> = trackedFilters(previousFilters);
  const next: Record<string, unknown> = trackedFilters(nextFilters);
  // find the diff between the 2 filters
  const diff = pickBy(next, (v, k) => !isEqual(previous[k], v));

  // log each filter change, unlikley this will ever be more than 1
  return Object.entries(diff).map(([key, value]) => ({
    name: EventNames.filterActioned,
    data: {
      "Context source": "Filter slide-out",
      "Filter name": key,
      "Filter selection": value,
      "Action type":
        Array.isArray(value) && value.length === 0 ? "Filter cleared" : "Filter applied",
    },
  }));
}

export function createGroupByChangedEvent(groupByName: string): TrackingEvent {
  return {
    name: EventNames.groupBySelected,
    data: {
      "Context source": "Tabs",
      "Selection name": groupByName,
    },
  };
}

export function createIntervalChangedEvent(interval: string): TrackingEvent {
  return {
    name: EventNames.dateUnitsSelected,
    data: {
      "Unit selected": interval,
    },
  };
}

export function createChartTypeChangedEvent(chartType: string): TrackingEvent {
  return {
    name: EventNames.chartTypeSelected,
    data: {
      "Context source": "Trends controls",
      "Chart type": chartType,
    },
  };
}

export function createViewModeChangedEvent(mode: string): TrackingEvent {
  return {
    name: EventNames.tabChanged,
    data: {
      "Context source": "Spend results",
      Tab: mode,
    },
  };
}

export function createDataExportedEvent(exportFormat: string, totalRows: number): TrackingEvent {
  return {
    name: EventNames.exportedData,
    data: {
      "Context source": "Table",
      "Data type": "Table",
      Format: exportFormat,
      Count: totalRows,
    },
  };
}
